import * as React from 'react';
import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { AuthProvider } from './auth/AuthContext';
import { LocationProvider } from './context/LocationContext';
import { Layout, AdminSection } from './components';
import { LocationFilter } from './config/eventsConfig';

// Add CSS animation for spinner and primary color variable
const spinnerStyle = document.createElement('style');
spinnerStyle.textContent = `
  :root {
    --primary-color: #0c3b5e;
    --primary-hover: #094561;
    --primary-light: rgba(12, 59, 94, 0.1);
    --error-color: #d32f2f;
    --error-light: rgba(211, 47, 47, 0.1);
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(spinnerStyle);

// Location-aware wrapper component
const LocationAwareLayout: React.FC = () => {
  const { location } = useParams<{ location: LocationFilter }>();
  const [activeSection, setActiveSection] = useState<'home' | 'schedule' | 'photos' | 'faq' | 'rsvp' | 'admin'>('home');
  
  // Validate location parameter
  const validLocation: LocationFilter = 
    location && ['all', 'pune', 'mumbai', 'main'].includes(location) 
      ? location as LocationFilter 
      : 'all';

  if (activeSection === 'admin') {
    return <AdminSection setActiveSection={setActiveSection} />;
  }

  return (
    <LocationProvider location={validLocation}>
      <Layout 
        activeSection={activeSection} 
        setActiveSection={setActiveSection}
      />
    </LocationProvider>
  );
};

// App wrapper that handles routing
const AppWrapper: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Default route redirects to /all */}
        <Route path="/" element={<Navigate to="/all" replace />} />
        
        {/* Location-based routes */}
        <Route path="/:location" element={<LocationAwareLayout />} />
        
        {/* Catch-all route redirects to /all */}
        <Route path="*" element={<Navigate to="/all" replace />} />
      </Routes>
    </Router>
  );
};

// Main App component with authentication provider
export default function App() {
  return (
    <AuthProvider>
      <FluentProvider theme={webLightTheme}>
        <AppWrapper />
      </FluentProvider>
    </AuthProvider>
  );
}
